import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Container,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import { motion } from 'framer-motion';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const CreateAssignmentScreen = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { schoolId, teacherId } = state || {};
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedClass, setSelectedClass] = useState('Grade 6');
  const [selectedSubject, setSelectedSubject] = useState('Mathematics');
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({
    questionText: '',
    options: ['', '', ''],
    correctAnswer: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const classes = [
    'Grade 1',
    'Grade 2',
    'Grade 3',
    'Grade 4',
    'Grade 5',
    'Grade 6'
  ];
  const subjects = [
    'Mathematics',
    'English',
    'Science',
    'History',
    'Geography'
  ];

  const addQuestion = () => {
    if (!currentQuestion.questionText.trim()) {
      setError('Question text is required');
      return;
    }

    if (
      questionType === 'multiple_choice' &&
      currentQuestion.options.some(opt => !opt.trim())
    ) {
      setError('All options are required for multiple choice');
      return;
    }

    const newQuestion = {
      questionType,
      questionText: currentQuestion.questionText,
      ...(questionType === 'multiple_choice' && {
        options: currentQuestion.options,
        correctAnswer: currentQuestion.correctAnswer
      })
    };

    setQuestions([...questions, newQuestion]);
    setCurrentQuestion({
      questionText: '',
      options: ['', '', ''],
      correctAnswer: ''
    });
    setError('');
  };

  const publishAssignment = async () => {
    if (!title.trim() || questions.length === 0) {
      setError('Title and at least one question are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(
        'https://kidsm.vercel.app/api/schools/assignments',
        {
          title,
          description,
          class: selectedClass,
          subject: selectedSubject,
          questions,
          schoolId,
          teacherId
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      navigate(-1);
    } catch (error) {
      console.error('Error publishing assignment:', error);
      setError(error.response?.data?.message || error.response?.data?.error || 'Failed to publish assignment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', pt: 8 }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <Typography
            variant="h4"
            sx={{ fontWeight: 'bold', color: '#333', mb: 4 }}
          >
            Create New Assignment
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ bgcolor: 'white', p: 3, borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <TextField
              fullWidth
              label="Assignment Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ mb: 3 }}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Description/Instructions"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={4}
              sx={{ mb: 3 }}
              variant="outlined"
            />
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                label="Class"
              >
                {classes.map((className) => (
                  <MenuItem key={className} value={className}>
                    {className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                label="Subject"
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject} value={subject}>
                    {subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}
            >
              Add Questions
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Question Type</InputLabel>
              <Select
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                label="Question Type"
              >
                <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                <MenuItem value="open_ended">Open Ended</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Question Text"
              value={currentQuestion.questionText}
              onChange={(e) =>
                setCurrentQuestion({ ...currentQuestion, questionText: e.target.value })
              }
              multiline
              rows={3}
              sx={{ mb: 3 }}
              variant="outlined"
            />
            {questionType === 'multiple_choice' && (
              <Box sx={{ mb: 3 }}>
                {[0, 1, 2].map((index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TextField
                      fullWidth
                      label={`Option ${index + 1}`}
                      value={currentQuestion.options[index]}
                      onChange={(e) => {
                        const newOptions = [...currentQuestion.options];
                        newOptions[index] = e.target.value;
                        setCurrentQuestion({ ...currentQuestion, options: newOptions });
                      }}
                      sx={{ mr: 2 }}
                    />
                    <Radio
                      checked={currentQuestion.correctAnswer === currentQuestion.options[index]}
                      onChange={() => {
                        if (currentQuestion.options[index]) {
                          setCurrentQuestion({
                            ...currentQuestion,
                            correctAnswer: currentQuestion.options[index]
                          });
                        }
                      }}
                      sx={{ color: '#6200ee', '&.Mui-checked': { color: '#6200ee' } }}
                    />
                  </Box>
                ))}
              </Box>
            )}
            <Button
              variant="contained"
              sx={{ bgcolor: '#6200ee', mb: 3, py: 2 }}
              onClick={addQuestion}
            >
              Add Question
            </Button>

            {questions.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}
                >
                  Added Questions ({questions.length})
                </Typography>
                {questions.map((q, i) => (
                  <Card key={i} sx={{ mb: 2, bgcolor: '#f5f5f5', borderRadius: '8px' }}>
                    <CardContent>
                      <Typography sx={{ fontSize: '16px', mb: 1 }}>
                        {i + 1}. {q.questionText}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                        {q.questionType === 'multiple_choice' ? 'Multiple Choice' : 'Open Ended'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            <Button
              variant="contained"
              fullWidth
              sx={{ bgcolor: '#4CAF50', py: 2 }}
              onClick={publishAssignment}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Publish Assignment'}
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CreateAssignmentScreen;